/**
 * Sync interne system_events → public_online_events/{slug}
 * Patient Zero: nur Marketing-Felder, keine Zoom-Links / Zielgruppen / Teilnehmer.
 */
import { doc, setDoc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { EventModel } from '../store/eventStore';
import { parseLocalEventDate, resolveEventRecording, toEventDateISO } from '../store/eventStore';
import { clarifyOnlineFormat, clarifyOnlineWording } from './onlineEventWording';

export const PUBLIC_EVENT_HOST = 'https://pe-one.friseur-coaching.de';

export type PublicSpeaker = {
  name: string;
  role?: string;
  bio?: string;
  imageUrl?: string;
};

export function slugifyEventTitle(title: string): string {
  const raw = String(title || '')
    .trim()
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
  return raw || 'event';
}

export function publicEventPreviewUrl(slug: string): string {
  const clean = String(slug || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
  return `${PUBLIC_EVENT_HOST}/event/${clean}`;
}

/** Nur für den Öffnen-Button: lokal localhost, sonst Produktiv-Domain. */
export function publicEventOpenUrl(slug: string): string {
  const clean = String(slug || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');
  const path = `/event/${clean}`;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') {
      return `${window.location.origin}${path}`;
    }
  }
  return `${PUBLIC_EVENT_HOST}${path}`;
}

function formatGermanDateLabel(isoOrRaw: string): string {
  const d = parseLocalEventDate(isoOrRaw);
  if (!d) return String(isoOrRaw || '').trim();
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTimeLabel(time: string): string {
  const t = String(time || '').trim();
  if (!t) return '';
  return t.includes('Uhr') ? t : `${t} Uhr`;
}

/** Marketing-Felder plus Zugangslink nur für die Bestätigungsmail — nicht auf der Landing rendern. */
export function buildPublicOnlineEventPayload(event: Partial<EventModel>): Record<string, unknown> {
  const activeDate = event.overrideDate || event.baseDate || '';
  const bullets = Array.isArray(event.publicBullets)
    ? event.publicBullets.map((b) => clarifyOnlineWording(String(b).trim())).filter(Boolean)
    : [];
  const speakerNameAliases: Record<string, string> = {
    'Martina Funk': 'Martina Jovanovic',
    Jean: 'Jean Standardt',
    'Jean Standard': 'Jean Standardt',
    Nelly: 'Nelly Rühle',
  };
  const speakers = Array.isArray(event.publicSpeakers)
    ? event.publicSpeakers
        .map((s) => {
          const raw = String(s?.name || '').trim();
          return {
            name: speakerNameAliases[raw] || raw,
            role: s?.role ? String(s.role).trim() : undefined,
            bio: s?.bio ? String(s.bio).trim() : undefined,
            imageUrl: s?.imageUrl ? String(s.imageUrl).trim() : undefined,
          };
        })
        .filter((s) => s.name)
        .map((s) => {
          const out: Record<string, string> = { name: s.name };
          if (s.role) out.role = s.role;
          if (s.bio) out.bio = s.bio;
          if (s.imageUrl) out.imageUrl = s.imageUrl;
          return out;
        })
    : [];

  const descriptionPlain = String(event.description || '')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
  // Landing-Teaser bleibt Plaintext; formatierte Monatsbeschreibung nur intern als HTML rendern
  const teaser =
    String(event.publicTeaser || '').trim() ||
    descriptionPlain ||
    String(event.subtitle || '').trim();

    const parsedActive = parseLocalEventDate(activeDate);
    return {
    title: String(event.title || 'Online-Event').trim(),
    subtitle: clarifyOnlineWording(String(event.subtitle || '').trim()) || undefined,
    date: formatGermanDateLabel(String(activeDate)),
    time: formatTimeLabel(String(event.timeMorning || event.timeEvening || '')),
    format: clarifyOnlineFormat(event.publicFormat || event.eventType || 'Online-Livestream'),
    teaser: clarifyOnlineWording(teaser) || undefined,
    bullets,
    speakers,
    ctaLabel: String(event.publicCtaLabel || 'Jetzt kostenfreien Platz sichern ❯').trim(),
    thankYouText: String(
      event.publicThankYouText ||
        'Dein Platz ist fest reserviert! Den Zugangslink findest du in der Bestätigungsmail.'
    ).trim(),
    heroImage: String(event.titleImage || event.imageUrl || '').trim() || undefined,
    willBeRecorded: resolveEventRecording(event),
    accessUrl: String(event.linkMorning || '').trim(),
    accessUrlEvening: event.hasDoubleSession ? String(event.linkEvening || '').trim() : '',
    isPublished: true,
    sourceSystemEventId: event.id ? String(event.id) : undefined,
    syncedAt: new Date().toISOString(),
    activeDateISO: parsedActive ? toEventDateISO(parsedActive) : undefined,
  };
}

export async function syncPublicOnlineEvent(opts: {
  slug: string;
  previousSlug?: string | null;
  event: Partial<EventModel>;
  publish: boolean;
}): Promise<void> {
  const slug = String(opts.slug || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (!slug) throw new Error('Public-Slug fehlt.');

  const prev = String(opts.previousSlug || '')
    .trim()
    .toLowerCase();

  if (!opts.publish) {
    await unpublishPublicOnlineEvent(slug);
    if (prev && prev !== slug) await unpublishPublicOnlineEvent(prev);
    return;
  }

  const payload = buildPublicOnlineEventPayload(opts.event);
  // Strip undefined (Firestore rejects undefined)
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(payload)) {
    if (v !== undefined) clean[k] = v;
  }

  await setDoc(doc(db, 'public_online_events', slug), clean, { merge: true });

  if (prev && prev !== slug) {
    await unpublishPublicOnlineEvent(prev);
  }
}

export async function unpublishPublicOnlineEvent(slug: string): Promise<void> {
  const clean = String(slug || '')
    .trim()
    .toLowerCase();
  if (!clean) return;
  const ref = doc(db, 'public_online_events', clean);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  try {
    await updateDoc(ref, { isPublished: false, unpublishedAt: new Date().toISOString() });
  } catch {
    await deleteDoc(ref);
  }
}
